import React, { useState, useEffect, useRef } from 'react';
import { Button } from './UIComponents';
import { Link2, Copy, Check, Clock, Eye, Trash2, X, Loader2, ChevronDown } from 'lucide-react';
import apiClient, { handleApiError } from '../api/client';
import { useTransition } from '../hooks/useTransition';

interface ShareResponse {
  id: string;
  transcription_id: string;
  token: string;
  url: string;
  expires_at: string | null;
  view_count: number;
  is_active: boolean;
  created_at: string;
}

type ExpirationOption = '1d' | '7d' | '30d' | 'permanent';

interface ShareDialogProps {
  transcriptionId: string;
  onClose: () => void;
  isOpen: boolean;
}

const EXPIRATION_OPTIONS: { value: ExpirationOption; label: string }[] = [
  { value: '1d', label: '1 day' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: 'permanent', label: 'No expiration' },
];

function buildPublicShareUrl(share: ShareResponse): string {
  if (typeof window === 'undefined') {
    return share.url;
  }
  return new URL(`/s/${share.token}`, window.location.origin).toString();
}

interface SimpleSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  className?: string;
}

const SimpleSelect: React.FC<SimpleSelectProps> = ({ value, onChange, options, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { shouldMount, isVisible } = useTransition(isOpen, 200);

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex w-full items-center justify-between px-4 py-2.5 rounded-xl border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-sm text-slate-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
          isOpen ? 'ring-2 ring-indigo-500' : ''
        }`}
      >
        <span className="truncate">{selectedOption?.label || 'Select...'}</span>
        <ChevronDown
          className={`h-4 w-4 text-slate-400 dark:text-zinc-500 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {shouldMount && (
        <div className={`absolute z-50 mt-1 w-full rounded-xl border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 shadow-lg overflow-hidden ${isVisible ? 'dropdown-in' : 'dropdown-out'}`}>
          <div className="py-1">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition-colors hover:bg-slate-50 dark:hover:bg-zinc-700 ${
                  value === option.value
                    ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 font-medium'
                    : 'text-slate-700 dark:text-zinc-300'
                }`}
              >
                <span>{option.label}</span>
                {value === option.value && <Check className="h-4 w-4 shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ShareDialog: React.FC<ShareDialogProps> = ({ transcriptionId, onClose, isOpen }) => {
  const { shouldMount, isVisible } = useTransition(isOpen, 200);
  const [share, setShare] = useState<ShareResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedExpiration, setSelectedExpiration] = useState<ExpirationOption>('7d');
  const [error, setError] = useState<string | null>(null);

  // Load existing share on mount
  useEffect(() => {
    if (isOpen) {
      loadShare();
    }
  }, [isOpen, transcriptionId]);

  const loadShare = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get<ShareResponse>(
        `/transcriptions/${transcriptionId}/share`
      );
      setShare(response.data);
    } catch {
      // No share exists, that's fine
      setShare(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateShare = async () => {
    setCreating(true);
    setError(null);
    try {
      const response = await apiClient.post<ShareResponse>(
        `/transcriptions/${transcriptionId}/share`,
        { expires_in: selectedExpiration }
      );
      setShare(response.data);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateExpiration = async () => {
    if (!share) return;
    setCreating(true);
    setError(null);
    try {
      const response = await apiClient.put<ShareResponse>(
        `/transcriptions/${transcriptionId}/share`,
        { expires_in: selectedExpiration }
      );
      setShare(response.data);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async () => {
    if (!confirm('Are you sure you want to revoke this share link? Anyone with the link will no longer be able to access it.')) {
      return;
    }
    setRevoking(true);
    setError(null);
    try {
      await apiClient.delete(`/transcriptions/${transcriptionId}/share`);
      setShare(null);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setRevoking(false);
    }
  };

  const handleCopy = async () => {
    if (!share) return;
    try {
      await navigator.clipboard.writeText(buildPublicShareUrl(share));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy to clipboard');
    }
  };

  const formatExpirationDate = (expiresAt: string | null) => {
    if (!expiresAt) return 'Never expires';
    const date = new Date(expiresAt);
    return `Expires ${date.toLocaleDateString()} at ${date.toLocaleTimeString()}`;
  };

  const isExpired = Boolean(share?.expires_at && new Date(share.expires_at) < new Date());
  const shareUrl = share ? buildPublicShareUrl(share) : '';

  if (!shouldMount) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className={`fixed inset-0 bg-black/50 ${isVisible ? 'modal-backdrop-in' : 'modal-backdrop-out'}`} onClick={onClose} />

      {/* Dialog */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className={`relative w-full max-w-lg rounded-2xl bg-white dark:bg-zinc-900 p-6 shadow-xl ${isVisible ? 'modal-content-in' : 'modal-content-out'}`}>
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-indigo-100 dark:bg-indigo-900/30">
                <Link2 className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-zinc-100">
                Share Transcription
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-zinc-800 transition-colors"
            >
              <X className="h-5 w-5 text-slate-500 dark:text-zinc-400" />
            </button>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
          ) : share && share.is_active ? (
            <div className="space-y-6">
              {/* Share URL */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300 mb-2">
                  Share Link
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={shareUrl}
                    className={`flex-1 px-4 py-2.5 rounded-xl border bg-slate-50 dark:bg-zinc-800 text-sm ${
                      isExpired
                        ? 'border-red-300 text-red-600 dark:border-red-700 dark:text-red-400'
                        : 'border-slate-200 dark:border-zinc-700 text-slate-900 dark:text-zinc-100'
                    }`}
                  />
                  <Button
                    variant="secondary"
                    onClick={handleCopy}
                    disabled={isExpired}
                    className="shrink-0"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {isExpired && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                    This link has expired. Update the expiration to reactivate it.
                  </p>
                )}
              </div>

              {/* Stats */}
              <div className="flex gap-6 py-4 px-4 bg-slate-50 dark:bg-zinc-800/50 rounded-xl">
                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4 text-slate-400" />
                  <span className="text-sm text-slate-600 dark:text-zinc-400">
                    {share.view_count} views
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-400" />
                  <span className={`text-sm ${
                    isExpired ? 'text-red-600 dark:text-red-400' : 'text-slate-600 dark:text-zinc-400'
                  }`}>
                    {formatExpirationDate(share.expires_at)}
                  </span>
                </div>
              </div>

              {/* Update Expiration */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300 mb-2">
                  Update Expiration
                </label>
                <div className="flex gap-2">
                  <SimpleSelect
                    value={selectedExpiration}
                    onChange={(val) => setSelectedExpiration(val as ExpirationOption)}
                    options={EXPIRATION_OPTIONS}
                    className="flex-1"
                  />
                  <Button variant="secondary" onClick={handleUpdateExpiration} isLoading={creating}>
                    Update
                  </Button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between pt-2">
                <Button
                  variant="ghost"
                  onClick={handleRevoke}
                  isLoading={revoking}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Revoke Link
                </Button>
                <Button variant="primary" onClick={onClose}>
                  Done
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Create new share */}
              <p className="text-slate-600 dark:text-zinc-400">
                Create a public link to share this transcription. Anyone with the link can view the
                transcript and any translations (read-only).
              </p>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300 mb-2">
                  Link Expiration
                </label>
                <SimpleSelect
                  value={selectedExpiration}
                  onChange={(val) => setSelectedExpiration(val as ExpirationOption)}
                  options={EXPIRATION_OPTIONS}
                  className="w-full"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleCreateShare} isLoading={creating}>
                  <Link2 className="h-4 w-4 mr-2" />
                  Create Share Link
                </Button>
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="mt-4 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareDialog;
